/**
 * PO Builder constraint enforcement — pure helpers (Day 98 P8.5).
 *
 * H.17 max_spend = ADVISORY (warn, never block); H.18 max_units_per_sku +
 * H.19 min_order_qty = HARD per-line blocks. Shared by POBuilder
 * (validate() + PO Summary breach line + ConfirmModal advisory) and
 * POLineItemsGrid (per-line Qty cell indicator). No React, no Supabase —
 * unit-tested in src/test/day98ConstraintEnforcement.test.ts.
 *
 * Resolution is the Day 98 Checkpoint-A hybrid: live in-session constraint
 * values (passed via nav-state from POTray) take precedence; the user's saved
 * buying_session_prefs constraint defaults (resolved via computeParamDefaults)
 * are the fallback. Unset / non-positive → null = "no limit" (skip enforcement).
 */

export interface ResolvedPoConstraints {
  /** H.17 — advisory ceiling on total staged PO spend. NULL = no cap. */
  maxSpend: number | null;
  /** H.18 — hard per-line ceiling on a single SKU's quantity. NULL = no cap. */
  maxUnitsPerSku: number | null;
  /** H.19 — hard per-line floor on a single SKU's quantity. NULL = no floor. */
  minOrderQty: number | null;
}

export type LineQtyViolation = 'over_max_units' | 'under_min_qty' | null;

/** Parse a raw constraint value to a positive number, or null (unset/≤0/NaN). */
function toPositive(raw: string | number | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Hybrid resolve (Checkpoint A): the live nav-state value wins, else the saved
 * per-user default. Both inputs are key→string maps (the BuyingSession
 * constraints state shape + computeParamDefaults output shape respectively).
 */
export function resolvePoConstraints(
  live: Record<string, string | undefined> | null | undefined,
  prefDefaults: Record<string, string> | null | undefined
): ResolvedPoConstraints {
  const pick = (key: string): number | null => toPositive(live?.[key] ?? prefDefaults?.[key]);
  return {
    maxSpend: pick('max_spend'),
    maxUnitsPerSku: pick('max_units_per_sku'),
    minOrderQty: pick('min_order_qty'),
  };
}

/**
 * Per-line hard check (H.18 + H.19). Returns the violation kind or null.
 * A null limit is skipped (no enforcement for that bound).
 */
export function checkLineQty(
  qty: number,
  maxUnitsPerSku: number | null,
  minOrderQty: number | null
): LineQtyViolation {
  if (maxUnitsPerSku != null && qty > maxUnitsPerSku) return 'over_max_units';
  if (minOrderQty != null && qty < minOrderQty) return 'under_min_qty';
  return null;
}

/** TONE_STANDARDS §3 inline field message for a per-line quantity violation. */
export function lineQtyMessage(
  violation: LineQtyViolation,
  maxUnitsPerSku: number | null,
  minOrderQty: number | null
): string | null {
  if (violation === 'over_max_units') return `Quantity must be ${maxUnitsPerSku} or fewer`;
  if (violation === 'under_min_qty') return `Quantity must be ${minOrderQty} or more`;
  return null;
}

/**
 * H.17 advisory metric: dollars the staged total is over the max_spend cap,
 * or null when there is no cap or the total is at/under it. Cents-rounded so
 * float accumulation can't produce a spurious sub-cent breach.
 */
export function maxSpendOverBy(totalCost: number, maxSpend: number | null): number | null {
  if (maxSpend == null) return null;
  const over = Math.round((totalCost - maxSpend) * 100) / 100;
  return over > 0 ? over : null;
}

// ───────────────────────────────────────────────────────────────────────────
// Day 98.1 — §11 risk classification + §10 guardrail evaluation (ADVISORY).
//
// All config-DEPENDENT computation lives here (client-side): the Day-98.1 RPC
// returns config-INDEPENDENT raw per-SKU fields, and these pure helpers classify
// risk, derive the by_risk 2-bucket split, and evaluate the 9 §10 guardrails — so
// a risk-config or threshold change recomputes instantly with no RPC refetch
// (`concentrationSig` is staged-rows-only). Advisory only — highlights/warns,
// never blocks (spec §10/§11). Unit-tested in src/test/day98_1RiskGuardrails.test.ts.
// ───────────────────────────────────────────────────────────────────────────

/** The 7 spec §11 risk factors: 3 live (data on disk) + 4 mark-unavailable. */
export type RiskFactorKey =
  | 'low_velocity'
  | 'high_competition'
  | 'low_demand'
  | 'high_returns'
  | 'amazon_as_seller'
  | 'stocking'
  | 'mfr_policy';

/** The 3 factors with usable data on disk (Day 98.1 G2). */
export const LIVE_RISK_FACTORS: readonly RiskFactorKey[] = [
  'low_velocity',
  'high_competition',
  'low_demand',
];

/** The 4 factors with no usable data (no column / all-empty on disk) — they
 *  NEVER trip regardless of the enabled toggle (their UI toggle renders
 *  disabled-with-reason per the Day-95 available:false + hint pattern). */
export const UNAVAILABLE_RISK_FACTORS: readonly RiskFactorKey[] = [
  'high_returns',
  'amazon_as_seller',
  'stocking',
  'mfr_policy',
];

/** The 9 spec §10 concentration guardrails. */
export type GuardrailKey =
  | 'max_pct_single_sku'
  | 'max_dollar_single_sku'
  | 'max_pct_brand'
  | 'max_pct_category'
  | 'max_pct_marketplace'
  | 'max_pct_high_risk'
  | 'max_pct_slow_movers'
  | 'max_pct_new'
  | 'min_skus';

export const GUARDRAIL_KEYS: readonly GuardrailKey[] = [
  'max_pct_single_sku',
  'max_dollar_single_sku',
  'max_pct_brand',
  'max_pct_category',
  'max_pct_marketplace',
  'max_pct_high_risk',
  'max_pct_slow_movers',
  'max_pct_new',
  'min_skus',
];

export interface RiskConfig {
  factors: Record<RiskFactorKey, { enabled: boolean }>;
  thresholds: { velocityFloor: number | null; competitionCeiling: number | null };
}

export interface SkuRiskResult {
  highRisk: boolean;
  /** The enabled+available factors this SKU tripped (OR semantics). */
  factors: RiskFactorKey[];
}

export interface GuardrailBreach {
  key: GuardrailKey;
  breached: boolean;
  /** Live metric in the guardrail's own unit: a percentage (0-100) for the
   *  max-% guardrails, dollars for max_dollar_single_sku, a SKU count for
   *  min_skus. Raw number — the view formats (1-dp % / currency / integer). */
  actual: number;
  /** Configured limit, same unit as `actual`. */
  limit: number;
}

/** A by_brand / by_category / by_marketplace bucket (also the by_risk shape).
 *  `title` is optional — only by_sku rows populate it (product title for the
 *  view's row tooltip); the aggregate axes leave it undefined. */
export interface ConcentrationBucket {
  label: string | null;
  title?: string | null;
  units: number;
  amount: number;
}

/** One by_sku row from calculate_po_concentration (Day 98.1 raw fields) — the
 *  single source of truth for the by_sku shape (usePoConcentration re-exports it
 *  and aligns POConcentrationResult.by_sku to it). */
export interface ConcentrationSkuRow extends ConcentrationBucket {
  amazon_monthly_sales: number | null;
  amazon_seller_count: number | null;
  amazon_demand_rating: string | null;
  is_new: boolean;
}

/** The deterministic portion of the calculate_po_concentration result the
 *  guardrail evals consume (the hook's POConcentrationResult is a structural
 *  superset). */
export interface Concentration {
  total: { units: number; amount: number };
  by_sku: ConcentrationSkuRow[];
  by_brand: ConcentrationBucket[];
  by_category: ConcentrationBucket[];
  by_marketplace: ConcentrationBucket[];
}

/**
 * Classify one staged SKU's risk: OR of the ENABLED + AVAILABLE §11 factors it
 * trips. The 4 unavailable factors never trip (no data); a disabled factor is
 * excluded; a NULL discriminator means that factor doesn't trip for that SKU; an
 * unset threshold means its factor can't trip. No composite score.
 */
export function classifySkuRisk(row: ConcentrationSkuRow, config: RiskConfig): SkuRiskResult {
  const f = config.factors;
  const { velocityFloor, competitionCeiling } = config.thresholds;
  const factors: RiskFactorKey[] = [];

  if (
    f.low_velocity?.enabled &&
    velocityFloor != null &&
    row.amazon_monthly_sales != null &&
    row.amazon_monthly_sales < velocityFloor
  ) {
    factors.push('low_velocity');
  }
  if (
    f.high_competition?.enabled &&
    competitionCeiling != null &&
    row.amazon_seller_count != null &&
    row.amazon_seller_count > competitionCeiling
  ) {
    factors.push('high_competition');
  }
  if (f.low_demand?.enabled && row.amazon_demand_rating === 'LOW') {
    factors.push('low_demand');
  }
  // high_returns / amazon_as_seller / stocking / mfr_policy: no usable data on
  // disk → never trip in V1 (deliberately not evaluated, regardless of enabled).

  return { highRisk: factors.length > 0, factors };
}

/**
 * The §10 "by risk level" axis: split staged SKUs into High risk / Not high risk
 * (summing units + amount). Always returns BOTH buckets ([0]=High, [1]=Not) — a
 * 0/0 bucket when one side is empty — so the view + the max-%-high-risk guardrail
 * have a stable shape.
 */
export function aggregateByRisk(
  rows: ConcentrationSkuRow[],
  config: RiskConfig
): ConcentrationBucket[] {
  let highUnits = 0;
  let highAmount = 0;
  let lowUnits = 0;
  let lowAmount = 0;
  for (const row of rows) {
    if (classifySkuRisk(row, config).highRisk) {
      highUnits += row.units;
      highAmount += row.amount;
    } else {
      lowUnits += row.units;
      lowAmount += row.amount;
    }
  }
  return [
    { label: 'High risk', units: highUnits, amount: highAmount },
    { label: 'Not high risk', units: lowUnits, amount: lowAmount },
  ];
}

/** Max bucket amount on an axis (0 for an empty axis). */
function maxAmount(buckets: ConcentrationBucket[]): number {
  let m = 0;
  for (const b of buckets) if (b.amount > m) m = b.amount;
  return m;
}

/**
 * Hybrid resolve for the §10 guardrail limits — the Checkpoint-A pattern,
 * symmetric with resolvePoConstraints (:39): the live in-session value wins per
 * key, else the saved per-user default. Scoped to the 9 GUARDRAIL_KEYS so no
 * filter / constraint key leaks into the guardrails arg. Returns the raw
 * key→string map; evaluateGuardrails does its own per-key toPositive (unset /
 * non-positive limits skip there, not here — no parsing in this helper).
 */
export function resolveGuardrails(
  live: Record<string, string | undefined> | null | undefined,
  prefDefaults: Record<string, string> | null | undefined
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const key of GUARDRAIL_KEYS) {
    out[key] = live?.[key] ?? prefDefaults?.[key];
  }
  return out;
}

/**
 * Evaluate the 9 §10 guardrails against the concentration result + the resolved
 * guardrail limits + risk config. ADVISORY — drives highlight/warn, never a
 * block. Only CONFIGURED guardrails (positive limit) are returned; an unset /
 * non-positive limit is skipped. An empty/zero tray (total.amount <= 0) returns
 * [] (no divide-by-zero, no breaches). Denominator = total.amount (the spend the
 * partner already sees). Percentages are 0-100 numbers; the view formats to 1-dp.
 */
export function evaluateGuardrails(
  concentration: Concentration,
  guardrails: Record<string, string | number | null | undefined>,
  config: RiskConfig
): GuardrailBreach[] {
  const total = concentration.total?.amount ?? 0;
  if (total <= 0) return [];

  const breaches: GuardrailBreach[] = [];
  const addPct = (key: GuardrailKey, amount: number): void => {
    const limit = toPositive(guardrails[key]);
    if (limit == null) return;
    const actual = (amount / total) * 100;
    breaches.push({ key, breached: actual > limit, actual, limit });
  };
  const addAbs = (key: GuardrailKey, actual: number, breachWhenBelow: boolean): void => {
    const limit = toPositive(guardrails[key]);
    if (limit == null) return;
    breaches.push({
      key,
      breached: breachWhenBelow ? actual < limit : actual > limit,
      actual,
      limit,
    });
  };

  const maxSku = maxAmount(concentration.by_sku);
  addPct('max_pct_single_sku', maxSku);
  addAbs('max_dollar_single_sku', maxSku, false); // breach when OVER the $ cap
  addPct('max_pct_brand', maxAmount(concentration.by_brand));
  addPct('max_pct_category', maxAmount(concentration.by_category));
  addPct('max_pct_marketplace', maxAmount(concentration.by_marketplace));
  addPct('max_pct_high_risk', aggregateByRisk(concentration.by_sku, config)[0].amount);

  // slow movers share the low-velocity floor; no floor → nothing is "slow" → 0.
  const floor = config.thresholds.velocityFloor;
  let slow = 0;
  if (floor != null) {
    for (const r of concentration.by_sku) {
      if (r.amazon_monthly_sales != null && r.amazon_monthly_sales < floor) slow += r.amount;
    }
  }
  addPct('max_pct_slow_movers', slow);

  let fresh = 0;
  for (const r of concentration.by_sku) if (r.is_new) fresh += r.amount;
  addPct('max_pct_new', fresh);

  addAbs('min_skus', concentration.by_sku.length, true); // breach when BELOW the floor

  return breaches;
}
